import React from 'react'
import { createHashRouter, RouteObject, Navigate } from 'react-router-dom'
import ErrorPage from './components/error-page'
import { getDefaultLayout } from './components/layout'
import { getAdminLayout } from './components/admin/dashboard-layout'
import { LoginPage } from './pages/auth/login'
import { ForgotPasswordPage } from './pages/auth/forgot-password'
import { ResetPasswordPage } from './pages/auth/reset-password'
import { CompleteInvitePage } from './pages/auth/complete-invite'
import { AuthErrorPage } from './pages/auth/auth-error'
import { UserInfoPage } from './pages/auth/user-info'
import WidgetsPage from './pages/widgets'
import { TOTPEnrollment } from './components/auth/totp-enrollment'
import { ProtectedRoute } from './components/protected-route'
import { AccountSettingsPage } from './pages/account/settings'

// Define public routes that don't require authentication
const publicPaths = [
  '/auth/reset-password',
  '/auth/forgot-password',
  '/auth/login',
  '/auth/complete-invite',
  '/auth/error',
  '/login',
]

export const routerObjects: RouteObject[] = [
  {
    path: '/auth/complete-invite',
    element: <CompleteInvitePage />,
    // Add this to ensure hash fragments are handled correctly
    children: [
      {
        path: '*',
        element: <CompleteInvitePage />,
      },
    ],
  },
  {
    path: '/auth/reset-password',
    element: <ResetPasswordPage />,
    // Add this to ensure hash fragments are handled correctly
    children: [
      {
        path: '*',
        element: <ResetPasswordPage />,
      },
    ],
  },
  {
    path: '/auth/login',
    element: <LoginPage />,
  },
  {
    path: '/login',
    element: <LoginPage />,
  },
  {
    path: '/auth/forgot-password',
    element: <ForgotPasswordPage />,
  },
  {
    path: '/auth/error',
    element: <AuthErrorPage />,
  },
  {
    // Root path - use Home component to handle authentication flow properly
    path: '/',
    Component: React.lazy(() => import('./pages/home')),
  },
  {
    path: '/admin',
    Component: React.lazy(() => import('./pages/admin')),
    children: [
      {
        path: '',
        Component: React.lazy(() => import('./pages/admin/dashboard')),
      },
      {
        path: 'users',
        Component: React.lazy(() => import('./pages/admin/users')),
      },
      {
        path: 'users/:id',
        Component: React.lazy(() => import('./pages/admin/users/[id]')),
      },
      {
        path: 'profile',
        Component: React.lazy(() => import('./pages/admin/profile')),
      },
      {
        path: 'audit',
        Component: React.lazy(() => import('./pages/admin/audit')),
      },
      {
        path: 'partners',
        Component: React.lazy(() => import('./pages/admin/partners')),
      },
      {
        path: 'partners/:id',
        Component: React.lazy(() => import('./pages/admin/partners/[id]')),
      },
      {
        path: 'merchants',
        Component: React.lazy(() => import('./pages/admin/merchants')),
      },
      {
        path: 'merchants/:id',
        Component: React.lazy(() => import('./pages/admin/merchants/[id]')),
      },
      {
        path: 'service-api-keys',
        Component: React.lazy(() => import('./pages/admin/service-api-keys')),
      },
      {
        path: 'transactions',
        Component: React.lazy(() => import('./pages/admin/transactions')),
      },
      {
        path: 'analytics',
        Component: React.lazy(() => import('./pages/admin/analytics')),
      },
      {
        path: 'merchant-analytics',
        Component: React.lazy(() => import('./pages/admin/merchant-analytics')),
      },
      {
        path: 'reports/daily-settlement',
        Component: React.lazy(() => import('./pages/admin/reports/daily-settlement')),
      },
      {
        path: 'reports/settlement-summary',
        Component: React.lazy(() => import('./pages/admin/reports/settlement-summary')),
      },
      // Add account settings as a child route of admin
      {
        path: 'account-settings',
        element: (() => {
          console.log('Router: Rendering account settings page within admin layout')
          return <AccountSettingsPage />
        })(),
      },
      // Redirect /admin/settings to /admin/account-settings
      {
        path: 'settings',
        element: (() => {
          console.log('Router: Redirecting from /admin/settings (child route) to /admin/account-settings')
          return <Navigate to="/admin/account-settings" replace />
        })(),
      },
    ],
  },
  {
    path: '/widgets',
    element: (
      <ProtectedRoute>
        <WidgetsPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/user-info',
    Component: UserInfoPage,
  },
  {
    path: '/totp-enrollment',
    Component: TOTPEnrollment,
  },
  {
    path: '/account/settings',
    element: (
      <ProtectedRoute>
        {(() => {
          console.log('Router: Rendering account settings page with default layout')
          return getDefaultLayout(<AccountSettingsPage />)
        })()}
      </ProtectedRoute>
    ),
  },
  {
    path: '/admin/settings',
    element: (() => {
      console.log('Router: Redirecting from /admin/settings to /account/settings')
      return <Navigate to="/account/settings" replace />
    })(),
  },
  {
    path: '*',
    element: <Navigate to="/" replace />,
  },
]

export function createRouter(): ReturnType<typeof createHashRouter> {
  console.log('[Router] Creating router with public paths:', publicPaths)

  // Create base routes without any wrappers
  const baseRoutes = routerObjects.map((route) => {
    // Don't wrap public routes with auth protection
    const isPublicRoute = publicPaths.some((path) => route.path === path || route.path?.startsWith(path + '/'))
    const requiresAuth = !isPublicRoute && route.path?.startsWith('/admin/')
    const requiresAdmin = route.path?.startsWith('/admin/')

    console.log('[Router] Processing route:', {
      path: route.path,
      isPublicRoute,
      requiresAuth,
      requiresAdmin,
      hasComponent: !!route.Component,
      hasElement: !!route.element,
    })

    let element = route.element
    const Component = route.Component

    if (Component) {
      const LazyComponent = Component
      type LayoutComponent = { getLayout?: typeof getDefaultLayout }
      const getLayout = (LazyComponent as LayoutComponent).getLayout || getDefaultLayout

      console.log('[Router] Setting up component:', {
        path: route.path,
        hasCustomLayout: !!(LazyComponent as LayoutComponent).getLayout,
        willUseDefaultLayout: !(LazyComponent as LayoutComponent).getLayout,
      })

      let content = (
        <React.Suspense fallback={<div>Loading...</div>}>
          <LazyComponent />
        </React.Suspense>
      )

      // For admin routes, wrap with admin layout
      if (route.path?.startsWith('/admin')) {
        console.log('[Router] Applying admin layout for path:', route.path)
        content = getAdminLayout(content)
      } else if (!isPublicRoute && route.path !== '/') {
        console.log('[Router] Applying default layout for path:', route.path)
        content = getLayout(content)
      } else {
        console.log('[Router] No layout applied for public path:', route.path)
      }

      if (requiresAuth) {
        console.log('[Router] Adding auth protection for path:', route.path, { requiresAdmin })
        content = <ProtectedRoute requireAdmin={requiresAdmin}>{content}</ProtectedRoute>
      }

      element = content
    }

    return {
      ...route,
      element: element || route.element,
      Component: null,
      ErrorBoundary: ErrorPage,
    }
  })

  // Log detailed route information
  console.log(
    '[Router] Final routes configuration:',
    baseRoutes.map((r) => ({
      path: r.path,
      hasErrorBoundary: !!r.ErrorBoundary,
      hasChildren: !!r.children?.length,
      isProtected: React.isValidElement(r.element) && r.element.type === ProtectedRoute,
    })),
  )

  const router = createHashRouter(baseRoutes)
  console.log('[Router] Router created successfully')
  return router
}
